import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormBuilder } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { Subscription, interval, switchMap, takeWhile, filter, from } from 'rxjs';

import { Client } from 'src/providers/client/client';
import { Integrations } from 'src/providers/integrations/integrations';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';

import {
  RemoteAccessAddress,
  RemoteAccessAddressChange,
  RemoteAccessDeviceWithAssignments,
  SeamService
} from 'src/shared/services/seam.service';

import { TidySelectNumberValueModel } from 'src/models/tidy-select-item.model';

interface DeviceRow {
  device: RemoteAccessDeviceWithAssignments;
  checkboxItem: { value: string; viewValue: string };
  checked: boolean;
  caption: string;
}

@Component({
  selector: 'manage-lock-assignments',
  templateUrl: './manage-lock-assignments.component.html',
  styleUrls: ['./manage-lock-assignments.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ManageLockAssignmentsComponent implements OnInit, OnDestroy {
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private cdr = inject(ChangeDetectorRef);
  private storage = inject(TidyStorage);
  private seamService = inject(SeamService);
  private client = inject(Client);
  private util = inject(Util);
  private sanitizer = inject(DomSanitizer);
  private destroyRef = inject(DestroyRef);
  private integrationsProvider = inject(Integrations);
  private formBuilder = inject(FormBuilder);
  private modalController = inject(ModalController);
  windowService = inject(WindowService);

  // Wizard state
  currentStep: 1 | 2 = 1;
  readonly totalSteps = 2;
  progressPercent = 50;
  stepHeaderTitle = 'Connect Your Smart Locks';
  // Drives chrome: the progress bar and step headers only render in the
  // onboarding flow (Getting Started). The chooser-entered flows are
  // minimal panels with just a dialog title and content.
  flowMode: 'onboarding' | 'updateExisting' | 'addEditSeam' | 'directConnect' = 'onboarding';
  showWizardChrome = true;

  // Step 1 (Connect Seam) state
  step1State: 'loading' | 'intro' | 'connecting' | 'saving' | 'connected' | 'error' = 'loading';
  seamWebviewUrl: SafeResourceUrl | null = null;
  errorMessage = '';
  isDisabling = false;
  providerName = '';
  providerImageUrl = '';
  private existingIntegrationId: number | null = null;
  private pollingSubscription: Subscription | null = null;

  // Step 2 (Select Locks) state
  step2State: 'loading' | 'ready' | 'empty' | 'error' = 'loading';
  step2ErrorMessage = '';
  saving = false;
  hasEdits = false;
  saveButtonLabel = 'Done';
  addressLocked = false;
  addressItems: TidySelectNumberValueModel[] = [];
  addressNameById: Record<number, string> = {};
  selectedAddressId: number | null = null;
  deviceRows: DeviceRow[] = [];
  private devices: RemoteAccessDeviceWithAssignments[] = [];

  addressForm = this.formBuilder.group({
    addressId: this.formBuilder.control<number | null>(null)
  });

  // Onboarding / Getting Started context
  fromGettingStarted = false;
  private addressId: number | null = null;
  private successRoute = 'getting-started';
  private readonly stepId = 'locks';

  // Edit tracking
  private localEdits = new Map<number, number[]>();
  private suppressAddressChange = false;
  private previousAddressId: number | null = null;
  // Tracks whether the user advanced from step 1 -> step 2 within this session.
  // False when they landed directly on step 2 (e.g. chooser "Update Existing"
  // or Getting Started with an existing integration).
  private cameFromStep1 = false;
  // True when the user entered via the chooser's "Add/Edit Seam Connections"
  // card. Drives a few small UI differences (no Next button on the connected
  // state, contextual dialog title).
  addSeamMode = false;

  async ngOnInit() {
    const params = await this.storage.retrieve('dialog-params');
    const paramAddressId: number | null = params?.addressId ?? null;
    const startOnStep1 = !!params?.startOnConnect;
    this.fromGettingStarted = !!params?.fromGettingStarted;
    this.successRoute = params?.successRoute || 'getting-started';
    this.flowMode = (params?.flowMode as typeof this.flowMode)
      || (this.fromGettingStarted ? 'onboarding' : (startOnStep1 ? 'directConnect' : 'updateExisting'));
    this.showWizardChrome = this.flowMode === 'onboarding';
    this.addSeamMode = this.flowMode === 'addEditSeam';
    this.rightSidePanelService.setDialogPageTitle(this.dialogTitleForMode());
    if (paramAddressId) {
      this.addressLocked = true;
      this.addressId = paramAddressId;
      this.selectedAddressId = paramAddressId;
    }
    await this.loadStep1(startOnStep1);
  }

  private dialogTitleForMode(): string {
    switch (this.flowMode) {
      case 'updateExisting': return 'Update Existing Assignments';
      case 'addEditSeam': return 'Add/Edit Seam Connections';
      case 'directConnect':
      case 'onboarding':
      default: return 'Connect Locks';
    }
  }

  ngOnDestroy() {
    this.rightSidePanelService.customBackAction$.next(null);
    this.pollingSubscription?.unsubscribe();
  }

  // ---------- Step 1: Connect Seam ----------

  private async loadStep1(startOnStep1: boolean) {
    this.step1State = 'loading';
    this.cdr.markForCheck();
    try {
      const integrations = await this.seamService.getExistingIntegration();
      if (integrations?.length > 0) {
        const integration = integrations[0];
        this.existingIntegrationId = integration.id;
        this.providerName = integration.integration_data?.account_type_display_name || '';
        this.providerImageUrl = integration.integration_data?.image_url || '';
        this.step1State = 'connected';
        if (!startOnStep1) {
          // User already connected — drop them on step 2 to assign locks.
          await this.goToStep2({ fromStep1: false });
          return;
        }
      } else {
        this.step1State = 'intro';
        if (this.fromGettingStarted || startOnStep1) {
          // Skip the intro screen and go straight to the Seam webview.
          this.connectLocks();
          return;
        }
      }
    } catch {
      this.step1State = 'intro';
    }
    this.refreshBackAction();
    this.cdr.markForCheck();
  }

  connectLocks = async () => {
    this.step1State = 'connecting';
    this.cdr.markForCheck();
    try {
      const webview = await this.seamService.createConnectWebview();
      this.seamWebviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(webview.url);
      this.cdr.markForCheck();
      this.startPolling(webview.connect_webview_id);
    } catch (err: any) {
      this.step1State = 'error';
      this.errorMessage = err?.error?.message || err?.message || 'Failed to start lock connection. Please try again.';
      this.cdr.markForCheck();
    }
  };

  private startPolling(connectWebviewId: string) {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = interval(3000).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(() => from(this.seamService.getConnectWebviewStatus(connectWebviewId))),
      takeWhile(status => status.status !== 'authorized', true),
      filter(status => status.status === 'authorized')
    ).subscribe({
      next: (status) => {
        this.onAuthorized(status.connected_account_id);
      },
      error: () => {
        this.step1State = 'error';
        this.errorMessage = 'Lost connection while checking lock status. Please try again.';
        this.seamWebviewUrl = null;
        this.cdr.markForCheck();
      }
    });
  }

  private async onAuthorized(connectedAccountId: string) {
    this.step1State = 'saving';
    this.seamWebviewUrl = null;
    this.cdr.markForCheck();
    try {
      await this.seamService.createIntegration(connectedAccountId);
    } catch (err: any) {
      const message = err?.error?.message || err?.message || '';
      const alreadyExists = message.toLowerCase().includes('already');
      if (!alreadyExists) {
        this.step1State = 'error';
        this.errorMessage = message || 'Failed to save lock integration. Please try again.';
        this.cdr.markForCheck();
        return;
      }
    }
    await this.storage.save('didMakeIntegrationChanges', true);
    await this.goToStep2({ fromStep1: true });
  }

  confirmAndDisable = async () => {
    if (!this.existingIntegrationId) {return;}
    const confirmParams: ConfirmPageParamsModel = {
      title: 'Disable Smart Locks?',
      body: 'Are you sure you want to disable your Seam integration? You will need to reconnect to use smart locks.',
      backText: 'Cancel',
      confirmText: 'Disable',
      confirmButtonClass: 'primary',
      confirmAction: async () => {
        await this.modalController.dismiss();
        await this.disableIntegration();
      }
    };
    const modal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: confirmParams,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await modal.present();
  };

  private disableIntegration = async () => {
    if (!this.existingIntegrationId) {return;}
    this.isDisabling = true;
    this.cdr.markForCheck();
    try {
      await this.integrationsProvider.deactivateIntegration(this.existingIntegrationId);
      await this.storage.save('didMakeIntegrationChanges', true);
      this.existingIntegrationId = null;
      this.util.showSuccess('Smart lock integration disabled.');
      await this.connectLocks();
    } catch (err: any) {
      this.util.showError(err?.error?.message || err?.message || 'Failed to disable integration.');
    } finally {
      this.isDisabling = false;
      this.cdr.markForCheck();
    }
  };

  retryStep1 = async () => {
    if (this.existingIntegrationId) {
      await this.loadStep1(true);
    } else {
      await this.connectLocks();
    }
  };

  skipStep = async () => {
    try {
      if (this.addressId) {
        await this.client.addMetadata({
          key: `onboarding_step.${this.addressId}.${this.stepId}`,
          value: 'completed'
        });
      }
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateForward('getting-started');
      }
    } catch (err) {
      this.util.showError('Failed to complete step. Please try again.');
    }
  };

  goToStep2 = async (opts: { fromStep1?: boolean } = { fromStep1: true }) => {
    this.cameFromStep1 = opts.fromStep1 ?? true;
    this.currentStep = 2;
    this.refreshBackAction();
    this.cdr.markForCheck();
    await this.loadStep2();
  };

  goToStep2FromNext = () => this.goToStep2({ fromStep1: true });

  // ---------- Step 2: Select Locks ----------

  private async loadStep2() {
    this.step2State = 'loading';
    this.step2ErrorMessage = '';
    this.cdr.markForCheck();
    try {
      const [addresses, devices, remoteAccessAddresses] = await Promise.all([
        this.client.getAddresses(),
        this.seamService.getRemoteAccessDevices(),
        this.seamService.getRemoteAccessAddresses()
      ]);
      this.addressItems = this.client.parseAddressList(addresses);
      this.addressNameById = {};
      for (const item of this.addressItems) {
        this.addressNameById[item.value] = item.viewValue;
      }
      this.devices = this.attachAssignments(devices, remoteAccessAddresses);
      if (this.selectedAddressId === null && this.addressItems.length > 0) {
        this.selectedAddressId = this.addressItems[0].value;
      }
      this.previousAddressId = this.selectedAddressId;
      this.addressForm.patchValue({ addressId: this.selectedAddressId }, { emitEvent: false });
      this.rebuildRows();
      this.step2State = this.devices.length === 0 ? 'empty' : 'ready';
      this.cdr.markForCheck();
    } catch (err: any) {
      this.step2State = 'error';
      this.step2ErrorMessage = this.extractErrorMessage(err, 'Unable to load your smart locks. Please try again.');
      this.cdr.markForCheck();
    }
  }

  // The devices endpoint does not return each lock's address assignments — the
  // many-to-many join lives on the remote-access-addresses endpoint, where each
  // RAA nests the Tidy addresses[] it is assigned to. Resolve every device's
  // assignments through its remote_access_address_id and default to [] so the
  // downstream filter()/includes() calls never see undefined.
  private attachAssignments(
    devices: RemoteAccessDeviceWithAssignments[],
    remoteAccessAddresses: RemoteAccessAddress[]
  ): RemoteAccessDeviceWithAssignments[] {
    const idsByRaaId = new Map<number, number[]>();
    for (const raa of remoteAccessAddresses) {
      idsByRaaId.set(raa.id, (raa.addresses ?? []).map(address => address.id));
    }
    return devices.map(device => ({
      ...device,
      address_ids: idsByRaaId.get(device.remote_access_address_id) ?? []
    }));
  }

  private extractErrorMessage(err: any, fallback: string): string {
    return err?.error?.error?.message
      || err?.error?.message
      || err?.message
      || fallback;
  }

  private effectiveAddressIds(device: RemoteAccessDeviceWithAssignments): number[] {
    const edit = this.localEdits.get(device.remote_access_address_id);
    return edit ?? device.address_ids;
  }

  private rebuildRows() {
    const selected = this.selectedAddressId;
    this.deviceRows = this.devices.map(device => {
      const ids = this.effectiveAddressIds(device);
      const others = selected === null ? ids : ids.filter(id => id !== selected);
      const caption = others.length > 0
        ? `Also at: ${others.map(id => this.addressNameById[id] || `#${id}`).join(', ')}`
        : '';
      const name = device.display_name || device.device_name;
      return {
        device,
        checkboxItem: { value: device.id.toString(), viewValue: name },
        checked: selected !== null && ids.includes(selected),
        caption
      };
    });
    this.hasEdits = this.localEdits.size > 0;
    this.saveButtonLabel = this.saving ? 'Saving...' : 'Done';
  }

  toggleDevice = (row: DeviceRow) => {
    if (this.selectedAddressId === null) {return;}
    const device = row.device;
    const current = this.effectiveAddressIds(device);
    const next = current.includes(this.selectedAddressId)
      ? current.filter(id => id !== this.selectedAddressId)
      : [...current, this.selectedAddressId];
    if (sameSet(next, device.address_ids)) {
      this.localEdits.delete(device.remote_access_address_id);
    } else {
      this.localEdits.set(device.remote_access_address_id, next);
    }
    this.rebuildRows();
    this.cdr.markForCheck();
  };

  onAddressSelectChange = async () => {
    if (this.suppressAddressChange) {return;}
    const nextId = Number(this.addressForm.controls.addressId.value);
    if (!nextId || nextId === this.selectedAddressId) {return;}
    if (this.localEdits.size > 0) {
      const choice = await this.confirmSwitchAddress();
      if (choice === 'save') {
        const saved = await this.saveInternal();
        if (!saved) {
          this.revertAddressSelection();
          return;
        }
      } else if (choice === 'discard') {
        this.localEdits.clear();
      } else {
        this.revertAddressSelection();
        return;
      }
    }
    this.selectedAddressId = nextId;
    this.previousAddressId = nextId;
    this.rebuildRows();
    this.cdr.markForCheck();
  };

  private revertAddressSelection() {
    this.suppressAddressChange = true;
    this.addressForm.patchValue({ addressId: this.previousAddressId }, { emitEvent: false });
    setTimeout(() => { this.suppressAddressChange = false; });
    this.cdr.markForCheck();
  }

  private async confirmSwitchAddress(): Promise<'save' | 'discard' | 'cancel'> {
    const alert = await this.util.showConfirmAlert({
      header: 'Unsaved Changes',
      message: 'Save your changes before switching properties?',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'Discard', role: 'destructive' },
        { text: 'Save', role: 'confirm' }
      ]
    });
    const { role } = await alert.onDidDismiss();
    if (role === 'confirm') {return 'save';}
    if (role === 'destructive') {return 'discard';}
    return 'cancel';
  }

  save = async () => {
    const saved = await this.saveInternal();
    if (!saved) {return;}
    if (this.fromGettingStarted && this.addressId) {
      try {
        await this.client.addMetadata({
          key: `onboarding_step.${this.addressId}.${this.stepId}`,
          value: 'completed'
        });
      } catch {
        // Non-blocking — metadata is best-effort
      }
    }
    if (this.addressLocked) {
      this.finishLockedFlow();
    } else {
      this.storage.save('didMakeIntegrationChanges', true);
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.back();
      }
    }
  };

  private async saveInternal(): Promise<boolean> {
    if (this.localEdits.size === 0) {return true;}
    this.saving = true;
    this.saveButtonLabel = 'Saving...';
    this.cdr.markForCheck();
    try {
      const changes: RemoteAccessAddressChange[] = [];
      for (const [raaId, address_ids] of this.localEdits) {
        changes.push({ id: raaId, address_ids });
      }
      await this.seamService.bulkUpdateRemoteAccessAddresses(changes);
      this.localEdits.clear();
      await this.refetchDevices();
      this.util.showSuccess('Lock assignments saved.');
      return true;
    } catch (err: any) {
      this.util.showError(this.extractErrorMessage(err, 'Unable to save lock assignments. Please try again.'));
      return false;
    } finally {
      this.saving = false;
      this.rebuildRows();
      this.cdr.markForCheck();
    }
  }

  private async refetchDevices() {
    try {
      const [devices, remoteAccessAddresses] = await Promise.all([
        this.seamService.getRemoteAccessDevices(),
        this.seamService.getRemoteAccessAddresses()
      ]);
      this.devices = this.attachAssignments(devices, remoteAccessAddresses);
      this.rebuildRows();
      this.cdr.markForCheck();
    } catch {
      // Non-blocking — UI still reflects the optimistic state
    }
  }

  private finishLockedFlow() {
    this.storage.save('didMakeIntegrationChanges', true);
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.closeRightSidePanel();
    } else if (this.successRoute) {
      this.navCtrl.navigateRoot(this.successRoute);
    } else {
      this.navCtrl.navigateRoot('getting-started');
    }
  }

  retryStep2 = async () => {
    await this.loadStep2();
  };

  // ---------- Wizard navigation ----------

  private refreshBackAction() {
    this.progressPercent = Math.round((this.currentStep / this.totalSteps) * 100);
    this.stepHeaderTitle = this.currentStep === 1
      ? 'Connect Your Smart Locks'
      : 'Assign Locks to Properties';
    if (this.currentStep === 2 && this.cameFromStep1) {
      this.rightSidePanelService.customBackAction$.next(() => this.goToStep1());
    } else {
      // Either we're on step 1, or the user landed directly on step 2 — let
      // the panel's default back behaviour (navigate to previous route /
      // close panel) take over.
      this.rightSidePanelService.customBackAction$.next(null);
    }
    // In chooser flows, the dialog title stays anchored to the entry context
    // throughout — only re-apply for safety in case some downstream code
    // mutates it.
    this.rightSidePanelService.setDialogPageTitle(this.dialogTitleForMode());
  }

  private goToStep1() {
    this.currentStep = 1;
    this.refreshBackAction();
    this.cdr.markForCheck();
  }

  mobileBackAction = (): boolean => {
    if (this.currentStep === 2 && this.cameFromStep1) {
      this.goToStep1();
      return false;
    }
    return true;
  };

  trackRow = (_index: number, row: DeviceRow) => `${row.device.id}-${this.selectedAddressId ?? 0}`;
}

function sameSet(a: number[], b: number[]): boolean {
  if (a.length !== b.length) {return false;}
  const sortedA = [...a].sort((x, y) => x - y);
  const sortedB = [...b].sort((x, y) => x - y);
  return sortedA.every((v, i) => v === sortedB[i]);
}
